import {
    CardPointeCardSecureBrowserTokenizationSessionSchema,
    buildCardPointeHostedIframeUrl,
    parseCardPointeHostedIframeMessage,
    resolveCardPointeHostedIframeExpiryDate,
} from '@pillar-portal/api-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { PortalHostedCardEntryProps } from '@/lib/ipg/card-entry';

export function CardPointeHostedInvoiceCardEntry({
    delegateToken,
    disabled,
    onTokenized,
    onError,
}: PortalHostedCardEntryProps) {
    const session = CardPointeCardSecureBrowserTokenizationSessionSchema.parse(delegateToken);
    const iframeUrl = useMemo(
        () => buildCardPointeHostedIframeUrl(session.card_secure_base_url),
        [session.card_secure_base_url]
    );
    const expectedOrigin = useMemo(() => new URL(iframeUrl).origin, [iframeUrl]);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const lastEmittedRef = useRef<string | null>(null);
    const [cardholderName, setCardholderName] = useState('');
    const [token, setToken] = useState<string | null>(null);
    const [tokenizationResult, setTokenizationResult] = useState<
        | Extract<
              ReturnType<typeof parseCardPointeHostedIframeMessage>,
              { outcome: 'tokenized' }
          >['tokenizationResult']
        | null
    >(null);

    useEffect(() => {
        const handleMessage = (event: MessageEvent<unknown>): void => {
            if (
                event.origin !== expectedOrigin ||
                event.source !== iframeRef.current?.contentWindow
            ) {
                return;
            }
            try {
                const result = parseCardPointeHostedIframeMessage(event.data);
                if (result.outcome === 'css_loaded') {
                    return;
                }
                if (result.outcome === 'pending') {
                    setToken(null);
                    setTokenizationResult(null);
                    return;
                }
                if (result.outcome === 'validation_error') {
                    setToken(null);
                    setTokenizationResult(null);
                    onError(result.message);
                    return;
                }
                setToken(result.token);
                setTokenizationResult(result.tokenizationResult);
            } catch (error: unknown) {
                onError(error instanceof Error ? error.message : 'Secure card entry failed.');
            }
        };
        window.addEventListener('message', handleMessage);
        return (): void => window.removeEventListener('message', handleMessage);
    }, [expectedOrigin, onError]);

    useEffect(() => {
        const normalizedName = cardholderName.trim();
        if (
            disabled ||
            token === null ||
            tokenizationResult === null ||
            normalizedName.length === 0
        ) {
            return;
        }
        const emittedKey = `${token}:${normalizedName}`;
        if (lastEmittedRef.current === emittedKey) {
            return;
        }
        lastEmittedRef.current = emittedKey;
        onTokenized({
            token,
            tokenizationResult,
            cardholderName: normalizedName,
            expiryDate: resolveCardPointeHostedIframeExpiryDate(
                tokenizationResult.provider_response
            ),
        });
    }, [cardholderName, disabled, onTokenized, token, tokenizationResult]);

    return (
        <div className="space-y-3">
            <div className="space-y-1">
                <label htmlFor="cardpointe-invoice-cardholder-name" className="text-sm font-medium">
                    Name on card
                </label>
                <input
                    id="cardpointe-invoice-cardholder-name"
                    autoComplete="cc-name"
                    disabled={disabled}
                    value={cardholderName}
                    onChange={(event) => setCardholderName(event.target.value)}
                    className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
            </div>
            <iframe
                ref={iframeRef}
                src={iframeUrl}
                title="CardPointe secure card entry"
                aria-label="Card number, expiration date, and CVV"
                className="min-h-40 w-full rounded-md border"
            />
        </div>
    );
}
